import {CustomerSupportRouteConstants} from './../../CustomerSupport/Routes';
import {all, call, put, takeLatest} from '@redux-saga/core/effects';
import customerSupportApi from 'Users/api/customerSupportApi';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {UserModel} from 'Users/models/UserModel';
import {
  changeCustomerSupport,
  CustomerSupportActionTypes,
  impersonateCustomerSuportUser,
  searchCustomerUsers,
  stopCustomerSupportImpersonation,
} from 'Users/ducks/CustomerSupport';
import {Application} from 'Users/models/Application';
import {extractApplicationIdsFromScopeSaga} from './applicationIdSaga';
import {Scope} from 'Shared/constants/Scope';
import {AsyncResult, fromResult, Loading, LoadingAdditional} from 'Core/models/Async';
import {pipe} from 'fp-ts/lib/pipeable';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import userApi from 'Users/api/userApi';
import {refreshToken} from 'Shared/utilities/Auth';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {head, findFirst} from 'fp-ts/lib/Array';
import * as O from 'fp-ts/lib/Option';

import {eq} from 'Shared/utilities/equals';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {setSelectedAgency, setUserClaims} from 'Users/ducks/userProfile';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AgentClaims} from 'Users/models/AgentClaims';
import {getIsNewYorkFromAgency} from 'Shared/utilities/isNewYorkUtility';
import {push} from 'connected-react-router';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {takeEvery} from 'redux-saga/effects';

function* watchCustomerSupportUsersSaga() {
  function* getCustomerSupportFromApi(action: ReturnType<typeof searchCustomerUsers>) {
    const {filter, pageOption, encryptedIdentifier, recruiterCodes} = action.payload;
    const agentApplicationIds = yield extractApplicationIdsFromScopeSaga(Scope.AgentSearchCustomerSupport);
    const enolltrackApplicationIds = yield extractApplicationIdsFromScopeSaga(Scope.EnrollTrackSearchCustomerSupport);
    const applicationIds =
      agentApplicationIds.length === 1 && enolltrackApplicationIds.length === 1
        ? [agentApplicationIds[0], enolltrackApplicationIds[0]]
        : agentApplicationIds.length === 1
        ? agentApplicationIds[0]
        : [Application.EnrollTrack];
    if (action.payload.pageOption) {
      yield put(changeCustomerSupport(new LoadingAdditional(action.payload.pageOption)));
    } else {
      yield put(changeCustomerSupport(new Loading(action.payload.pageOption)));
    }
    const result: AsyncResult<Page<UserModel>> = yield pageOption
      ? call(mergeWithNextPage, {existingPage: pageOption})
      : call(
          customerSupportApi.searchCustomerSupportUsers,
          applicationIds,
          filter,
          encryptedIdentifier,
          recruiterCodes
        );

    yield pipe(
      result,
      E.map(fromResult),
      E.fold(
        value => put(changeCustomerSupport(value)),
        value => put(changeCustomerSupport(value))
      )
    );
  }
  yield takeLatest(CustomerSupportActionTypes.SEARCH_CUSTOMER_SUPPORTS, getCustomerSupportFromApi);
}
export function* loadAgentClaims(agentClaims: AgentClaims) {
  const defaultAgency = pipe(agentClaims.agencyAssociations, head);

  const isNewYork = getIsNewYorkFromAgency(defaultAgency);
  yield put(setSelectedAgency(defaultAgency, isNewYork));
  yield put(push(CoreRouteConstants.DASHBOARD));
}

function* impersonateCustomerSupportUserSaga(action: ReturnType<typeof impersonateCustomerSuportUser>) {
  yield call(userApi.impersonate, action.payload.username, action.payload.impersonatedCustomerSupportUsername);

  yield call(refreshToken);

  const newClaims = pipe(getUserClaims());

  if (O.isSome(newClaims)) {
    const impersonatedAgentClaims = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
      O.chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.AgentSite]: agentClaims => O.fromNullable(agentClaims.impersonatedAgentClaim),
          default: () => O.none,
        })
      )
    );

    const impersonatedEnrollTrackClaims = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.EnrollTrack)),
      O.chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.EnrollTrack]: enrollTrackClaims =>
            O.fromNullable(enrollTrackClaims.impersonatedEnrollTrackClaim),
          default: () => O.none,
        })
      )
    );

    if (O.isSome(impersonatedAgentClaims)) {
      yield loadAgentClaims(impersonatedAgentClaims.value);
      yield put(
        createSnackbar(
          `Emulating ${impersonatedAgentClaims.value?.firstName} ${impersonatedAgentClaims.value?.lastName}`,
          true
        )
      );
    } else if (O.isSome(impersonatedEnrollTrackClaims)) {
      
      if(impersonatedEnrollTrackClaims.value.agencyAssociations){

        const defaultAgency = pipe(impersonatedEnrollTrackClaims.value.agencyAssociations, head);
 
        const isNewYork = getIsNewYorkFromAgency(defaultAgency);
 
        yield put(setSelectedAgency(defaultAgency, isNewYork));
      }

      yield put(push(CoreRouteConstants.DASHBOARD));
      yield put(
        createSnackbar(
          `Emulating ${impersonatedEnrollTrackClaims.value.firstName} ${impersonatedEnrollTrackClaims.value.lastName} `,
          true
        )
      );
    }

    yield put(setUserClaims(fromResult(newClaims.value)));
  }
}
function* watchImpersonateCustomerSupportUserSaga() {
  yield takeLatest(CustomerSupportActionTypes.START_IMPERSONATE_CUSTOMER_SUPPORT, impersonateCustomerSupportUserSaga);
}

export function* stopImpersonationApi(action: ReturnType<typeof stopCustomerSupportImpersonation>) {
  yield call(userApi.stopImpersonation, action.payload.username);

  yield call(refreshToken);

  const newClaims = pipe(getUserClaims());
  if (O.isSome(newClaims)) {
    yield put(createSnackbar(`Emulation has stopped.`, true));
    yield put(setUserClaims(fromResult(newClaims.value)));
    yield put(setSelectedAgency(O.none, false));
  }

  yield put(push(CustomerSupportRouteConstants.CUSTOMER_SUPPORT));
}

export function* stopImpersonationSaga() {
  yield takeEvery(CustomerSupportActionTypes.STOP_IMPERSONATION, stopImpersonationApi);
}
export default function*() {
  yield all([watchCustomerSupportUsersSaga(), watchImpersonateCustomerSupportUserSaga(), stopImpersonationSaga()]);
}
